import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  type LexicalEditor,
  type RangeSelection,
} from 'lexical'
import { type EffectiveTextStyle } from '@backend/lib/db/typeRoots/layoutNode'
import { $isRichTextNode, RichTextNode } from './RichTextNode'
import { type SavedRange } from './EditorContext'

/** Apply a partial style to whatever range is currently selected in `editor`.
 *  Splits any RichTextNode that the selection only partially covers, then
 *  rewrites the in-range nodes' style fields. Caret-only selections fall
 *  through unchanged.
 *
 *  Pass `restore` to handle the case where the toolbar click stole focus
 *  before the user got to act on the range — Radix popovers portal out of
 *  the toolbar wrapper, so the contenteditable loses focus and Lexical's
 *  live selection collapses. The toolbar caches the last live range and
 *  passes it back here so the click on a color swatch still hits the span
 *  the user had highlighted.
 *
 *  Critically: when applying via `restore`, we DO NOT call `$setSelection`.
 *  Lexical's reconciliation syncs DOM selection at end-of-update, which
 *  would force focus onto the contenteditable — Radix interprets that as
 *  `onFocusOutside` and dismisses the open picker. We split and style by
 *  key-traversal instead, leaving the selection state untouched. */
export const applyStyleToSelection = (
  editor: LexicalEditor,
  partial: Partial<EffectiveTextStyle>,
  restore?: SavedRange | null,
): void => {
  editor.update(
    () => {
      const selection = $getSelection()
      if ($isRangeSelection(selection) && !selection.isCollapsed()) {
        // Live range — extract() splits at boundaries and returns the
        // in-range nodes. The DOM-selection sync that follows is fine
        // here because the editor already has focus.
        for (const node of selection.extract()) {
          if ($isRichTextNode(node)) node.setRunStyle(partial)
        }
        return
      }
      if (restore) applyStyleByKeyRange(restore, partial)
    },
    { discrete: true },
  )
}

/** Walk the editor by node-key from `restore.anchorKey/anchorOffset` to
 *  `restore.focusKey/focusOffset`, splitting RichTextNodes at those offsets
 *  and applying the style. Clears the live selection up front because
 *  Lexical's `splitText` internally rewrites selection points that fell
 *  inside the split node — that internal rewrite triggers the same DOM
 *  selection sync at end-of-update that explicit `$setSelection` would,
 *  and the resulting focus shift dismisses the open Radix popover. With
 *  selection set to null, splitText has nothing to adjust and the
 *  popover keeps focus.
 *
 *  Must run inside an editor.update transaction. */
function applyStyleByKeyRange(restore: SavedRange, partial: Partial<EffectiveTextStyle>): void {
  $setSelection(null)
  const anchorNode = $getNodeByKey(restore.anchorKey)
  const focusNode = $getNodeByKey(restore.focusKey)
  if (!anchorNode || !focusNode) return
  if (!$isTextNode(anchorNode) || !$isTextNode(focusNode)) return

  // Order anchor/focus by document position. Both are inside the same
  // paragraph for this editor's single-block usage; use index-within-parent
  // as the tiebreaker for cross-node ranges.
  const aIdx = anchorNode.getIndexWithinParent()
  const fIdx = focusNode.getIndexWithinParent()
  let startNode = anchorNode
  let startOff = restore.anchorOffset
  let endNode = focusNode
  let endOff = restore.focusOffset
  if (aIdx > fIdx || (aIdx === fIdx && restore.anchorOffset > restore.focusOffset)) {
    startNode = focusNode
    startOff = restore.focusOffset
    endNode = anchorNode
    endOff = restore.anchorOffset
  }

  if (startNode === endNode) {
    if (startOff === endOff) return // collapsed; nothing to do
    const target = sliceMiddle(startNode, startOff, endOff)
    if (target instanceof RichTextNode) target.setRunStyle(partial)
    return
  }

  // Cross-node range. Split start at startOff (keep tail half + onward),
  // split end at endOff (keep up to and including head half).
  const startTail = takeTail(startNode, startOff)
  const endHead = takeHead(endNode, endOff)
  if (!startTail || !endHead) return

  // Walk siblings from startTail through endHead inclusive, applying to
  // every RichTextNode encountered.
  let cursor: ReturnType<typeof startTail.getNextSibling> | typeof startTail | null = startTail
  while (cursor) {
    if (cursor instanceof RichTextNode) cursor.setRunStyle(partial)
    if (cursor === endHead) break
    cursor = cursor.getNextSibling()
  }
}

/** Split `node` at `start` and `end`, return the middle piece. */
function sliceMiddle(node: ReturnType<typeof $getNodeByKey>, start: number, end: number) {
  if (!node || !$isTextNode(node)) return null
  const size = node.getTextContentSize()
  // Lexical's splitText(...offsets) ignores 0 and `size` boundaries, so
  // we pass only "interior" offsets and figure out which returned piece
  // is the middle.
  const offsets: number[] = []
  if (start > 0) offsets.push(start)
  if (end < size) offsets.push(end)
  if (offsets.length === 0) return node // whole-node range
  const splits = node.splitText(...offsets)
  if (start > 0 && end < size) return splits[1] ?? null
  if (start === 0) return splits[0] ?? null
  return splits[1] ?? null
}

/** Split `node` at `offset` and return the second half (in-range portion). */
function takeTail(node: ReturnType<typeof $getNodeByKey>, offset: number) {
  if (!node || !$isTextNode(node)) return null
  const size = node.getTextContentSize()
  if (offset >= size) return null
  if (offset <= 0) return node
  const splits = node.splitText(offset)
  return splits[1] ?? null
}

/** Split `node` at `offset` and return the first half (in-range portion). */
function takeHead(node: ReturnType<typeof $getNodeByKey>, offset: number) {
  if (!node || !$isTextNode(node)) return null
  const size = node.getTextContentSize()
  if (offset <= 0) return null
  if (offset >= size) return node
  const splits = node.splitText(offset)
  return splits[0] ?? null
}

/** Read a representative style for the current selection. Returns the style
 *  of the first RichTextNode in range — a useful proxy for "what the toolbar
 *  should show." A future iteration could detect mixed values and surface
 *  them, but for now this matches Figma's pick-the-anchor behavior. */
export const readSelectionStyle = (selection: RangeSelection): EffectiveTextStyle | null => {
  const anchor = selection.anchor.getNode()
  let cursor: ReturnType<typeof anchor.getParent> | typeof anchor | null = anchor
  while (cursor) {
    if (cursor instanceof RichTextNode) return cursor.getRunStyle()
    cursor = cursor.getParent()
  }
  // Fall back to the first selected RichTextNode if the anchor isn't one.
  for (const n of selection.getNodes()) {
    if (n instanceof RichTextNode) return n.getRunStyle()
  }
  return null
}
